<template>
<div class="page-product">
  <transition name="slide-sidebar-modal">
    <modal-view ref="detailModal" v-show="productModalIsActive" @dismiss="toggleModal">
      <Preloader v-if="productIsLoading" class="h-full"/>
      <template v-else>
        <template v-if="hasProduct">
          <product-detail-card :product="product"
                               :category="category"
                               :promotion-id="actualPromotionId"
                               :reward-id="actualRewardId"
                               @close="toggleModal"
          />
        </template>
        <template v-else>
          <div class="p-4">
            <h1 class="text-3xl font-bold mb-4">Товар не найден</h1>
            <p>К сожалению, указанный товар не найден. Убедитесь, что ссылка корректна.</p>
          </div>
        </template>
      </template>
    </modal-view>
  </transition>
</div>
</template>

<script>
import ModalView from '@/components/modals/ModalView'
import ProductDetailCard from '@/components/products/ProductDetailCard'
import axios from '@/zenky'
import config from '@/config'
import withModals from '@/mixins/withModals'
import InjectsSeo from '@/mixins/InjectsSeo'
import cachedState from '@/cachedState'
import zenkyEvents from '@/events'

export default {
  mixins: [withModals, InjectsSeo],

  props: {
    productId: {
      required: true,
      type: String,
    },

    promotionId: {
      required: false,
      type: String,
      default: '',
    },

    rewardId: {
      required: false,
      type: String,
      default: '',
    },
  },

  components: {
    Preloader: () => import('@/components/preloaders/Preloader'),
    ModalView,
    ProductDetailCard,
  },

  metaInfo() {
    if (this.hasProduct) {
      return this.getMetaInfoFrom(this.product.seo, 'Каталог товаров')
    }

    return {
      title: this.getFullPageTitle('Каталог товаров'),
    }
  },

  created() {
    if (this.$route.params && this.$route.params.product) {
      this.product = this.$route.params.product
      this.detectCategory()
    } else {
      this.loadProduct()
    }
  },

  mounted() {
    setTimeout(() => this.productModalIsActive = true, 100)
  },

  computed: {
    productRequestQuery() {
      return {
        with: config.includes.productItem,
      }
    },

    hasProduct() {
      return !!this.product && !!this.product.id
    },

    hasPromotion() {
      return !!this.promotionId && !!this.rewardId
    },

    hasPromotionInQuery() {
      if (!this.$route.query) {
        return false
      }

      return !!this.$route.query.promotion_id && !!this.$route.query.reward_id
    },

    actualPromotionId() {
      if (this.promotionId) {
        return this.promotionId
      } else if (this.hasPromotionInQuery) {
        return this.$route.query.promotion_id
      }

      return null
    },

    actualRewardId() {
      if (this.rewardId) {
        return this.rewardId
      } else if (this.hasPromotionInQuery) {
        return this.$route.query.reward_id
      }

      return null
    },
  },

  methods: {
    toggleModal() {
      this.productModalIsActive = !this.productModalIsActive

      if (!this.productModalIsActive) {
        setTimeout(() => { this.$router.back() }, 300)
      }
    },

    loadProduct() {
      if (this.productIsLoading) {
        return
      } else if (this.productLoadedFromCachedState()) {
        return
      }

      this.productIsLoading = true

      axios.get(`products/${this.productId}`, { params: this.productRequestQuery })
        .then(({ data }) => {
          this.productIsLoading = false

          if (data.data.promotion_reward && (!this.hasPromotion && !this.hasPromotionInQuery)) {
            this.product = {}

            return
          } else if (data.data.hidden) {
            this.product = {}

            return
          }

          this.product = data.data

          this.detectCategory()
        })
        .catch(err => {
          this.productIsLoading = false
          console.error(err)
        })
    },

    productLoadedFromCachedState() {
      const product = cachedState.product()

      if (product === null || !product.id || (product.id !== this.productId && product.short_id !== this.productId)) {
        return false
      }

      this.product = product
      this.detectCategory()

      return true
    },

    detectCategory() {
      if (this.$route.params.category) {
        this.category = this.$route.params.category
      } else if (this.product && this.product.categories && this.product.categories.length > 0) {
        this.category = this.product.categories[0]
      }
    },
  },

  watch: {
    productId() {
      this.productModalIsActive = true
      this.productIsLoading = false
    },

    '$route'(route) {
      if (route.params && route.params.product) {
        this.product = route.params.product
      } else {
        this.loadProduct()
      }
    },

    product: {
      immediate: true,
      handler(product) {
        if (!product || !product.id) {
          return
        }

        this.$root.sharedEventBus.$emit(zenkyEvents.PRODUCT_OPENED, {
          product,
        })
      },
    },
  },

  data() {
    return {
      productModalIsActive: false,
      productIsLoading: false,
      product: {},
      category: {},
      modalDescriptors: [
        {
          isActive: 'productModalIsActive',
          id: this,
        },
      ],
    }
  },
}
</script>
